export default function PolyAlphasSection() {
  return (
    <section className="bg-background-100 py-20 md:py-28">
      <div className="w-full px-4 md:px-8 lg:px-16">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium mb-5">
              <i className="ri-flask-line text-sm w-4 h-4 flex items-center justify-center" />
              Alianza Técnica Estratégica
            </div>
            <h2 className="font-heading text-xl sm:text-2xl md:text-4xl font-bold text-foreground-950 mb-3 md:mb-4">
              Formulación Validada en{' '}
              <span className="text-primary-500">Laboratorio y Campo</span>
            </h2>
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed max-w-3xl mx-auto text-justify">
              Desde el año 2022, RESINVENCA RESINAS DE VENEZUELA C.A. ha trabajado de la mano
              con <a
                href="https://www.polyalphas.net/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="font-semibold text-foreground-800 hover:text-primary-500 underline underline-offset-2 transition-colors"
              >POLY ALPHAS CHEMICALS C.A.</a>,
              quien a su vez trabaja con <a
                href="https://rixingroup.cn/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="font-semibold text-foreground-800 hover:text-primary-500 underline underline-offset-2 transition-colors"
              >Rixin Corp</a>{' '}
              (China) con su formulación especializada. Con la asesoría técnica y química de Poly Alphas,
              hemos desarrollado la formulación adecuada de demulsificantes, realizando estudios
              rigurosos en cada campo petrolero y validando resultados en laboratorio para
              garantizar la máxima efectividad en la separación de agua y crudo.
            </p>
            {/* Links oficiales */}
            <div className="mt-5 flex flex-col sm:flex-row items-center justify-center gap-3">
              <a
                href="https://www.polyalphas.net/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-secondary-500 text-background-50 text-sm font-semibold hover:bg-secondary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-external-link-line" />
                Visitar Poly Alphas Chemicals
              </a>
              <a
                href="https://rixingroup.cn/"
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-external-link-line" />
                Visitar Rixin Corp
              </a>
            </div>
          </div>

          {/* Video + Info grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            {/* Video */}
            <div className="order-1 lg:order-1">
              <div className="relative rounded-xl overflow-hidden bg-background-200 aspect-[4/3]">
                <video
                  src="https://storage.helloreaddy.io/project_files/9755469e-cfe9-4755-b8ae-44d6e19e4537/26a3166e-a9b6-4ce1-b24c-3d19dde3cd9d_WhatsApp-Video-2026-07-28-at-1.25.00-PM.mp4"
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  muted
                  loop
                  autoPlay
                  aria-label="Video de formulaciones de demulsificante en laboratorio"
                />
              </div>
              <p className="text-foreground-400 text-xs mt-3 text-center">
                Formulaciones de demulsificante validadas en laboratorio —{' '}
                <a href="https://www.polyalphas.net/" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-primary-500 underline underline-offset-2 transition-colors">Poly Alphas Chemicals</a>{' '}
                +{' '}
                <a href="https://rixingroup.cn/" target="_blank" rel="noopener noreferrer nofollow" className="hover:text-primary-500 underline underline-offset-2 transition-colors">Rixin Corp</a>
              </p>
              {/* Credit: Enderson Cruz */}
              <div className="mt-4 flex items-center gap-3 p-3 rounded-lg bg-background-50 border border-background-200/60">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-600 flex-shrink-0">
                  <i className="ri-user-star-line text-base" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-foreground-950">Enderson Cruz</p>
                  <p className="text-xs text-foreground-500">Poly Alphas Chemicals C.A.</p>
                </div>
              </div>
            </div>

            {/* Info cards */}
            <div className="order-2 lg:order-2 space-y-5">
              {[
                {
                  icon: 'ri-team-line',
                  title: 'Cadena de Colaboración: RESINVENCA → Poly Alphas → Rixin Corp',
                  desc: 'Poly Alphas Chemicals C.A. aporta su asesoría técnica y química directa desde el 2022, trabajando con la formulación de Rixin Corp (China) para adaptarla a las condiciones de los campos petroleros venezolanos.',
                },
                {
                  icon: 'ri-flask-line',
                  title: 'Estudios de Laboratorio Rigurosos',
                  desc: 'Cada formulación pasa por pruebas de compatibilidad, estabilidad y efectividad antes de salir al campo. Validación científica en cada lote con respaldo de Rixin Corp.',
                },
                {
                  icon: 'ri-map-pin-range-line',
                  title: 'Validación en Cada Campo Petrolero',
                  desc: 'Desde 2022 hemos realizado estudios específicos en los campos de la Faja Petrolífera del Orinoco, ajustando la formulación de Rixin Corp a las condiciones reales de cada yacimiento.',
                },
                {
                  icon: 'ri-shield-check-line',
                  title: 'Resultados Comprobados',
                  desc: 'La formulación adecuada logra separación eficiente de agua y crudo, reduciendo tiempos de tratamiento y costos operativos en planta. Respaldada por estudios de Poly Alphas y Rixin Corp.',
                },
              ].map((item) => (
                <div
                  key={item.title}
                  className="flex items-start gap-4 p-4 md:p-5 rounded-lg bg-background-50 border border-background-200/60"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className={`${item.icon} text-base`} />
                  </div>
                  <div>
                    <h4 className="font-heading font-semibold text-sm text-foreground-950 mb-1">
                      {item.title}
                    </h4>
                    <p className="text-foreground-600 text-sm leading-relaxed text-justify">
                      {item.desc}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}